import { Check, Play, Lock, ChevronDown } from 'lucide-react';

import type { AnnotatedModule } from './learnModel';
import { LearnTopicRow } from './LearnTopicRow';
import { ModuleSummaryStrip } from './ModuleSummaryStrip';

const ACTION: Record<string, string> = {
  completed: 'Review', in_progress: 'Continue', unlocked: 'Open Module', locked: 'Locked',
};

function StatusIcon({ state }: { state: AnnotatedModule['state'] }) {
  if (state === 'locked') {
    return (
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#fff3ee]">
        <Lock className="size-3.5 text-[#ff5a1f]" />
      </span>
    );
  }
  if (state === 'in_progress' || state === 'unlocked') {
    return (
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#ede9fe]">
        <Play className="size-3.5 translate-x-px fill-[#8b5cf6] text-[#8b5cf6]" />
      </span>
    );
  }
  return (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#fff3ee]">
      <Check className="size-4 text-[#ff5a1f]" />
    </span>
  );
}

function ActionButton({ state }: { state: AnnotatedModule['state'] }) {
  const label = ACTION[state];
  if (state === 'completed') {
    return <span className="rounded-[14px] border border-[#e5e7eb] px-[17px] py-[9px] text-[13px] font-semibold text-[#374151]">{label}</span>;
  }
  if (state === 'locked') {
    return <span className="rounded-[14px] border border-[#e5e7eb] bg-[#f9fafb] px-[17px] py-[9px] text-[13px] font-semibold text-[#9ca3af]">{label}</span>;
  }
  return <span className="rounded-[14px] bg-[#ede9fe] px-4 py-2 text-[13px] font-semibold text-[#8b5cf6]">{label}</span>;
}

export function ModuleRow({ batchId, index, annotated, open, onToggle }: {
  batchId: string; index: number; annotated: AnnotatedModule; open: boolean; onToggle: () => void;
}) {
  const { module, state, pct, topics } = annotated;
  const locked = state === 'locked';
  const expandable = !locked;

  return (
    <div className="border-b border-[#f3f4f6] last:border-b-0">
      <button
        type="button"
        onClick={expandable ? onToggle : undefined}
        aria-expanded={expandable ? open : undefined}
        disabled={!expandable}
        className={`flex w-full items-center gap-4 px-6 py-4 text-left ${expandable ? '' : 'cursor-not-allowed'}`}
      >
        <StatusIcon state={state} />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-[#111827]">{index}. {module.title}</p>
          {module.description && <p className="pt-0.5 text-sm text-[#6b7280]">{module.description}</p>}
        </div>
        <div className="hidden w-36 flex-col items-end gap-1 sm:flex">
          <span className={`text-sm font-semibold ${pct === 0 ? 'text-[#9ca3af]' : 'text-[#111827]'}`}>{pct}% Complete</span>
          <span className="text-xs text-[#9ca3af]">{module.completed_count} / {module.topic_count} topics</span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <ActionButton state={state} />
          {expandable && <ChevronDown className={`size-4 text-[#9ca3af] transition-transform ${open ? 'rotate-180' : ''}`} />}
        </div>
      </button>

      {open && expandable && (
        <div className="bg-[#fafafa] pb-4 pl-[80px] pr-6">
          <ModuleSummaryStrip batchId={batchId} module={module} pct={pct} />
          <div className="flex flex-col gap-0.5">
            {topics.map((at) => (
              <LearnTopicRow key={at.topic.id} batchId={batchId} annotated={at} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
